import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import GroupForm from './GroupForm';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);

  const fetchGroups = () => {
    api.get('/groups').then(res => setGroups(res.data));
  };

  const fetchPermissions = () => {
    api.get('/groups/permissions/all').then(res => setAllPermissions(res.data));
  };

  useEffect(() => {
    fetchGroups();
    fetchPermissions();
  }, []);

  const handleSave = (group) => {
    if (editing?._id) {
      api.put(`/groups/${editing._id}`, group).then(() => {
        setShowForm(false);
        setEditing(null);
        fetchGroups();
      });
    } else {
      api.post('/groups', group).then(() => {
        setShowForm(false);
        setEditing(null);
        fetchGroups();
      });
    }
  };

  const handleClone = (group) => {
    api.post(`/groups/${group._id}/clone`).then(() => fetchGroups());
  };

  const handleDelete = (group) => {
    if (window.confirm(`Are you sure you want to delete the group "${group.name}"?`)) {
      api.delete(`/groups/${group._id}`).then(() => fetchGroups());
    }
  };

  const formatPermissions = (permissions) => {
    permissions = permissions || [];
    if (permissions.length === 0) return 'No Permissions';
    if (permissions.length === allPermissions.length) return 'All Permissions';
    
    // Group permissions by category for better display
    const grouped = {
      'Dashboard': permissions.filter(p => p.includes('Dashboard')),
      'Users': permissions.filter(p => p.includes('User')),
      'Groups': permissions.filter(p => p.includes('Group')),
      'Forms': permissions.filter(p => p.includes('Form')),
      'Master Data': permissions.filter(p => p.includes('Master Data')),
      'Tasks': permissions.filter(p => p.includes('Task') && !p.includes('Time Log')),
      'Time Logs': permissions.filter(p => p.includes('Time Log')),
      'Admin Settings': permissions.filter(p => 
        p.includes('Client Type') || p.includes('Product') || p.includes('Upload Field') || 
        p.includes('Verification Type') || p.includes('Email Gateway') || p.includes('Invoice Settings')
        || p.includes('Block')
      ),
      'Documents': permissions.filter(p => p.includes('Document')),
      'Reports': permissions.filter(p => p.includes('Report')),
      'System': permissions.filter(p => p.includes('System')),
      'Other': permissions.filter(p => 
        !p.includes('Dashboard') && !p.includes('User') && !p.includes('Group') && 
        !p.includes('Form') && !p.includes('Master Data') && !p.includes('Task') &&
        !p.includes('Client Type') && !p.includes('Product') && !p.includes('Upload Field') &&
        !p.includes('Verification Type') && !p.includes('Email Gateway') && !p.includes('Invoice Settings') &&
        !p.includes('Block') && !p.includes('Document') && !p.includes('Report') && !p.includes('System') &&
        !p.includes('Time Log')
      )
    };

    const activeCategories = Object.entries(grouped)
      .filter(([category, perms]) => perms.length > 0)
      .map(([category, perms]) => `${category} (${perms.length})`);
    
    return activeCategories.join(', ');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Group Management</h1>
          <p className="text-gray-600 mt-1">Manage user groups and their permissions</p>
        </div>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          onClick={() => { setEditing({}); setShowForm(true); }}
        >
          + Add Group
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.map(group => (
              <tr key={group._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{group.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-md">
                    {formatPermissions(group.permissions)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs">
                    {group.description || 'No description'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition-colors"
                      onClick={() => { setEditing(group); setShowForm(true); }}
                      title="Edit Group"
                    >
                      Edit
                    </button>
                    <button
                      className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md transition-colors"
                      onClick={() => handleClone(group)}
                      title="Clone Group"
                    >
                      Clone
                    </button>
                    <button
                      className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md transition-colors"
                      onClick={() => handleDelete(group)}
                      title="Delete Group"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {groups.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">No groups found</div>
            <div className="text-gray-500 mt-2">Create your first group to get started</div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl">
            <GroupForm
              group={editing}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups; 